// Types related to Artists and Artist Aliases

/**
 * Basic Artist information.
 */
export interface Artist {
	id: string;
	name: string;
	bio: string | null;
	manual_genres: string[] | null; // Manual genres to supplement Spotify genres (array format)
	image_url: string | null;
	website_url: string | null;
	instagram: string | null;
	twitter: string | null;
	spotify_url: string | null;
	created_at: string;
	updated_at: string;
	spotify_artist_id?: string | null;
	spotify_genres?: string[] | null;
}

/**
 * Artist Alias information.
 */
export interface ArtistAlias {
	id: string;
	artist_id: string;
	alias_name: string;
	is_primary: boolean;
	created_at: string;
	updated_at: string;
}

/**
 * Artist with aliases included for complete representation.
 */
export interface ArtistWithAliases extends Artist {
	aliases: ArtistAlias[];
	name: string;
	image_url: string | null;
	manual_genres: string[] | null;
}

/**
 * Search result that can match either the primary artist name or an alias.
 * This is used by the artist alias search functionality, not the global search.
 */
export interface ArtistAliasSearchResult {
	id: string;
	name: string; // The matched name (could be primary or alias)
	primary_name: string; // The canonical/primary artist name
	is_alias_match: boolean; // Whether the match was on an alias
	computed_primary_genre: string | null;
	image_url: string | null;
}

/**
 * RPC response type for artist search with aliases.
 */
export type ArtistSearchRpcResponse = {
	artist_id: string;
	artist_name: string;
	artist_image_url: string | null;
	manual_genres: string[] | null;
};

/**
 * Utility function to get the display name for an artist in a specific context.
 * For example, when showing a VOYD set, we might want to display "VOYD"
 * even though the primary artist is "SVDDEN DEATH".
 */
export function getArtistDisplayName(
	artist: ArtistWithAliases,
	preferredAlias?: string,
): string {
	if (!preferredAlias) {
		// Return primary name
		for (const alias of artist.aliases) {
			if (alias.is_primary) {
				return alias.alias_name;
			}
		}
		return artist.name;
	}

	// Check if the preferred alias exists
	for (const alias of artist.aliases) {
		if (alias.alias_name.toLowerCase() === preferredAlias.toLowerCase()) {
			return alias.alias_name;
		}
	}

	return artist.name;
}

/**
 * Utility function to get all alias names for an artist.
 */
export function getAllAliases(artist: ArtistWithAliases): string[] {
	return artist.aliases.map((alias) => alias.alias_name);
}

/**
 * Utility function to get non-primary aliases for display.
 */
export function getSecondaryAliases(artist: ArtistWithAliases): string[] {
	return artist.aliases
		.filter((alias) => !alias.is_primary)
		.map((alias) => alias.alias_name);
}
